import { defineComponent as _defineComponent } from 'vue'
import { os } from 'bits/browser'
import { __ } from 'bits/intl'
import { HomePageCard, homepageCards } from 'bits/landside_helper'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from 'library/v4/components/OzContainedButton.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import { storeToRefs } from 'pinia'
import { useWindowSizeStore } from 'pinia/window_size'
import { computed, nextTick, onMounted, ref, watch } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'HomePageWelcomeCard',
  setup(__props) {

const {
  isSmallerThanDesktop,
  isSmallerThanDesktopBig,
  isSmallerThanDesktop2XL,
  isSmallerThanTabletPortrait,
  isSmallerThanTabletLandscape,
  windowWidth,
} = storeToRefs(useWindowSizeStore())
const welcomeCard = ref<HTMLElement>()
const welcomeCardText = ref<HTMLHeadingElement>()
const signupButton = ref<InstanceType<typeof OzContainedButton>>()
const installAppButton = ref<InstanceType<typeof OzContainedButton>>()
const installAppMaxWidth = ref(0)

const isOsSupported = computed(() => os() !== 'unknown')
const installAppUrl = computed(() => {
  if (os() === 'ios') return 'https://padlet.com/ios-app'
  if (os() === 'android') return 'https://padlet.com/android-app'
  if (os() === 'windows') return 'https://www.microsoft.com/en-us/p/padlet/9mtwd1kmhb48'
  if (os() === 'mac') return 'https://padlet.com/mac-app-download'
  return undefined
})
const installAppIcon = computed(() => {
  if (os() === 'ios' || os() === 'mac') return 'apple'
  if (os() === 'android') return 'android'
  if (os() === 'windows') return 'windows'
  return undefined
})
const installAppText = computed(() => {
  if (isSmallerThanDesktop.value) return __('Install app')
  if (os() === 'ios') return __('Install app')
  if (os() === 'android') return __('Install app')
  if (os() === 'windows') return __('Install Windows app')
  if (os() === 'mac') return __('Install Mac app')
  return undefined
})

onMounted(() => nextTick(handleOverflow))

watch(windowWidth, handleOverflow)

function handleOverflow() {
  // We encapsulate all functions that are used to handle overflows here
  handleInstallAppButtonOverflow()
  handleWelcomeCardTextOverflow()
}

// The following consts are used in
// both handleInstallAppButtonOverflow and handleWelcomeCardTextOverflow
const welcomeCardSidePaddingTabletPortrait = 48 // 24px x 2
const welcomeCardSidePaddingTabletLandscape = 64 // 32px x 2
const welcomeCardSidePaddingDesktop2Xl = 96 //  48px x 2

function handleInstallAppButtonOverflow(): void {
  if (!welcomeCard.value || !signupButton.value || !installAppButton.value) return

  const welcomeCardSidePaddingMobile = 0
  const spaceBetweenSignupAndInstallAppButtonMobile = 12
  const spaceBetweenSignupAndInstallAppButtonDesktop2Xl = 16
  const installAppButtonPadding = 36 // 16px start + 20px end

  let welcomeCardSidePadding = welcomeCardSidePaddingDesktop2Xl
  if (isSmallerThanTabletPortrait.value) {
    welcomeCardSidePadding = welcomeCardSidePaddingMobile
  } else if (isSmallerThanTabletLandscape.value) {
    welcomeCardSidePadding = welcomeCardSidePaddingTabletPortrait
  } else if (isSmallerThanDesktop.value) {
    welcomeCardSidePadding = welcomeCardSidePaddingTabletLandscape
  } else if (isSmallerThanDesktop2XL.value) {
    welcomeCardSidePadding = welcomeCardSidePaddingTabletLandscape
  }

  // Special case for mobile where the welcome card exceeds the windowWidth
  let welcomeCardWidth = welcomeCard.value.clientWidth
  if (welcomeCardWidth > windowWidth.value) {
    welcomeCardWidth = windowWidth.value
    welcomeCardSidePadding = 32 // 16px * 2
  }

  const spaceBetweenSignupAndInstallAppButton = isSmallerThanDesktop2XL.value
    ? spaceBetweenSignupAndInstallAppButtonMobile
    : spaceBetweenSignupAndInstallAppButtonDesktop2Xl

  const maxWidthForInstallAppButton =
    welcomeCardWidth -
    signupButton.value.$el.clientWidth -
    spaceBetweenSignupAndInstallAppButton -
    welcomeCardSidePadding -
    installAppButtonPadding

  installAppMaxWidth.value = maxWidthForInstallAppButton / 16 // 1rem = 16px
}

function handleWelcomeCardTextOverflow(): void {
  if (!welcomeCard.value || !welcomeCardText.value) return

  const fontClasses = [
    'welcome-card-text-marketing-1',
    'welcome-card-text-marketing-2',
    'welcome-card-text-marketing-3',
    'welcome-card-text-marketing-4',
    'welcome-card-text-marketing-5',
    'welcome-card-text-heading-1',
    'welcome-card-text-heading-1-large',
    'welcome-card-text-heading-2',
    'welcome-card-text-heading-2-large',
    'welcome-card-text-heading-3',
    'welcome-card-text-heading-4',
  ]

  // Reset the font-size to the original font-size.
  // We have to manually remove all welcome-card-* classes applied previously
  welcomeCardText.value.classList.remove(...fontClasses)

  const welcomeCardHeight = welcomeCard.value.clientHeight

  const welcomeCardSidePaddingMobile = 32 // 16px x 2
  const buttonFrameHeight = 40
  const buttonFrameHeightDesktop = 64

  const textAndButtonGap = 12
  const textAndButtonGapDesktop2Xl = 24

  let welcomeCardSidePaddingSize: number
  let buttonFrameHeightSize: number
  let textAndButtonGapSize: number

  if (isSmallerThanTabletPortrait.value) {
    welcomeCardSidePaddingSize = welcomeCardSidePaddingMobile
    buttonFrameHeightSize = 0 // In mobile, the welcome card text height does not include the button frame
    textAndButtonGapSize = 0 // In mobile, the welcome card text height does not include the gap between the text and button
  } else if (isSmallerThanTabletLandscape.value) {
    welcomeCardSidePaddingSize = welcomeCardSidePaddingTabletPortrait
    buttonFrameHeightSize = buttonFrameHeight
    textAndButtonGapSize = textAndButtonGap
  } else if (isSmallerThanDesktop.value) {
    welcomeCardSidePaddingSize = welcomeCardSidePaddingTabletLandscape
    buttonFrameHeightSize = buttonFrameHeight
    textAndButtonGapSize = textAndButtonGap
  } else if (isSmallerThanDesktopBig.value) {
    welcomeCardSidePaddingSize = welcomeCardSidePaddingTabletLandscape
    buttonFrameHeightSize = buttonFrameHeightDesktop
    textAndButtonGapSize = textAndButtonGap
  } else if (isSmallerThanDesktop2XL.value) {
    welcomeCardSidePaddingSize = welcomeCardSidePaddingTabletLandscape
    buttonFrameHeightSize = buttonFrameHeightDesktop
    textAndButtonGapSize = textAndButtonGap
  } else {
    welcomeCardSidePaddingSize = welcomeCardSidePaddingDesktop2Xl
    buttonFrameHeightSize = buttonFrameHeightDesktop
    textAndButtonGapSize = textAndButtonGapDesktop2Xl
  }

  const availableHeightForWelcomeText =
    welcomeCardHeight - welcomeCardSidePaddingSize - buttonFrameHeightSize - textAndButtonGapSize

  // Resize the welcome text using our pre-defined font classes until it is within the available space
  for (const index in fontClasses) {
    welcomeCardText.value.classList.add(fontClasses[index])
    // We use scrollHeight since we apply overflow-hidden to the text
    if (welcomeCardText.value.scrollHeight <= availableHeightForWelcomeText) {
      break
    } else {
      welcomeCardText.value.classList.remove(fontClasses[index])
    }
  }
}

return { __sfc: true,isSmallerThanDesktop, isSmallerThanDesktopBig, isSmallerThanDesktop2XL, isSmallerThanTabletPortrait, isSmallerThanTabletLandscape, windowWidth, welcomeCard, welcomeCardText, signupButton, installAppButton, installAppMaxWidth, isOsSupported, installAppUrl, installAppIcon, installAppText, handleOverflow, welcomeCardSidePaddingTabletPortrait, welcomeCardSidePaddingTabletLandscape, welcomeCardSidePaddingDesktop2Xl, handleInstallAppButtonOverflow, handleWelcomeCardTextOverflow, __, HomePageCard, homepageCards, OzContainedButton, OzContainedButtonColorScheme, OzContainedButtonSizePreset, OzIcon }
}

})